import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import { useDryRunPanel } from "./useDryRunPanel";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { RefreshCw, Unlock } from "lucide-react";

const CATS = ["PRODUCT_IDENTITY", "MATERIALS", "ORIGIN", "REPAIR_CARE", "END_OF_LIFE", "FOOTPRINT", "CERTIFICATIONS"];

function Stat({ label, value, testId }) {
  return (
    <div className="p-5 bg-white border" style={{ borderColor: "var(--bp-border)" }} data-testid={testId}>
      <div className="uppercase text-xs tracking-widest mb-2" style={{ color: "var(--bp-text-muted)" }}>{label}</div>
      <div className="font-serif text-4xl">{value}</div>
    </div>
  );
}

function LaneStat({ label, value, tone, testId, hideWhenZero }) {
  if (hideWhenZero && !value) return null;
  // Tone drives the accent color: `rescue` for free recovery lanes (win!),
  // `paid` for paid-provider recoveries (money spent), `neutral` for direct.
  const accent =
    tone === "rescue" ? "#2A5F4F" :
    tone === "paid" ? "#8A5A2B" :
    "var(--bp-text-secondary)";
  const dimmed = !value;
  return (
    <div
      className="p-3 border"
      style={{
        borderColor: "var(--bp-border)",
        background: dimmed ? "transparent" : "#FAFAF8",
        opacity: dimmed ? 0.55 : 1,
      }}
      data-testid={testId}
    >
      <div className="uppercase text-[10px] tracking-widest mb-1" style={{ color: "var(--bp-text-muted)" }}>{label}</div>
      <div className="font-serif text-2xl leading-none" style={{ color: accent }}>{value ?? 0}</div>
    </div>
  );
}

const STATE_CHIP_COLOR = {
  QUEUED: "bg-slate-100 text-slate-700",
  RUNNING: "bg-blue-100 text-blue-800",
  AWAITING_APPROVAL: "bg-amber-100 text-amber-800",
  NEEDS_ATTENTION: "bg-orange-100 text-orange-800",
  FAILED: "bg-rose-100 text-rose-800",
  PUBLISHED: "bg-emerald-100 text-emerald-800",
  PUBLISHED_ACCESS_RESTRICTED: "bg-sky-100 text-sky-800",
  SUCCEEDED: "bg-emerald-100 text-emerald-800",
  other: "bg-neutral-100 text-neutral-700",
};

function relativeTime(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return iso;
  const s = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function AdminOps() {
  const [d, setD] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [newBlock, setNewBlock] = useState({ domain: "", reason: "" });
  const [worker, setWorker] = useState(null);
  const [testUrl, setTestUrl] = useState("https://example.com");
  const [testResult, setTestResult] = useState(null);
  const [testBusy, setTestBusy] = useState(false);

  const [health, setHealth] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [readinessBusy, setReadinessBusy] = useState(false);
  const [recentBatches, setRecentBatches] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [coverageErr, setCoverageErr] = useState(false);
  const [composePath, setComposePath] = useState(null);
  const [composePathErr, setComposePathErr] = useState(false);
  const [rateLimit, setRateLimit] = useState(null);
  const [migStatus, setMigStatus] = useState(null);
  const [consoleHealth, setConsoleHealth] = useState(null);
  const [migLog, setMigLog] = useState(null);
  const [recrawlBusy, setRecrawlBusy] = useState(false);
  const [recrawlProgress, setRecrawlProgress] = useState(null); // { runIds, done, total, sector }
  const [sectorBacklog, setSectorBacklog] = useState(null);
  const [brandsNoWebsite, setBrandsNoWebsite] = useState(null);
  // Dry-run panel state (form, result, history, run handler) is
  // owned by a dedicated hook. See useDryRunPanel.js.
  const {
    form: dryRun, setForm: setDryRun,
    result: dryRunResult, setResult: setDryRunResult,
    history: dryRunHistory,
    busy: dryRunBusy,
    runDryRun,
    applyHistoryRow,
  } = useDryRunPanel();
  const nav = useNavigate();
  const load = useCallback(() => {
    http.get("/admin/ops/dashboard").then((r) => setD(r.data));
    http.get("/admin/pipeline/config").then((r) => setCfg(r.data));
    http.get("/admin/ops/worker-status").then((r) => setWorker(r.data)).catch(() => setWorker({ error: "endpoint unavailable" }));
    http.get("/admin/ops/data-health").then((r) => setHealth(r.data)).catch(() => setHealth(null));
    http.get("/admin/ops/readiness").then((r) => setReadiness(r.data)).catch(() => setReadiness(null));
    http.get("/admin/ops/recent-bulk-batches?days=7").then((r) => setRecentBatches(r.data)).catch(() => setRecentBatches(null));
    http.get("/admin/ops/coverage-by-lane?hours=24")
      .then((r) => { setCoverage(r.data); setCoverageErr(false); })
      .catch(() => setCoverageErr(true));
    http.get("/admin/ops/compose-path-stats?days=7")
      .then((r) => { setComposePath(r.data); setComposePathErr(false); })
      .catch(() => setComposePathErr(true));
    http.get("/admin/ops/rate-limit-hits")
      .then((r) => setRateLimit(r.data))
      .catch(() => setRateLimit(null));
    http.get("/admin/ops/migration-status")
      .then((r) => setMigStatus(r.data))
      .catch(() => setMigStatus(null));
    http.get("/admin/ops/console-health")
      .then((r) => setConsoleHealth(r.data))
      .catch(() => setConsoleHealth(null));
    http.get("/admin/ops/migration-history-log?name=backfill_console_state_fields&limit=10")
      .then((r) => setMigLog(r.data))
      .catch(() => setMigLog(null));
    http.get("/admin/ops/sector-backlog")
      .then((r) => setSectorBacklog(r.data))
      .catch(() => setSectorBacklog(null));
    http.get("/admin/ops/brands-without-website")
      .then((r) => setBrandsNoWebsite(r.data))
      .catch(() => setBrandsNoWebsite(null));
  }, []);

  // Recrawl progress — polls the enqueued run IDs every 4s until all
  // are terminal, then clears the toast.
  useEffect(() => {
    if (!recrawlProgress || recrawlProgress.done >= recrawlProgress.total) return undefined;
    const timer = setInterval(async () => {
      try {
        const { data } = await http.get(
          `/admin/pipeline/runs-status?ids=${recrawlProgress.runIds.join(",")}`,
        );
        const TERMINAL = new Set(["SUCCEEDED", "FAILED", "NEEDS_ATTENTION", "AWAITING_APPROVAL", "HELD", "PUBLISHED_ACCESS_RESTRICTED"]);
        const done = (data.runs || []).filter((r) => TERMINAL.has(r.state)).length;
        setRecrawlProgress((p) => (p ? { ...p, done } : p));
      } catch { /* transient; keep polling */ }
    }, 4000);
    return () => clearInterval(timer);
  }, [recrawlProgress]);

  const runRecrawlNoRun = async (sector = null) => {
    const scope = sector ? sector : "all NO_RUN";
    if (!window.confirm(`Enqueue a MANUAL pipeline run for every ${scope} brand without any prior run? Brands with a website will be crawled.`)) return;
    setRecrawlBusy(true);
    try {
      const body = { limit: 50 };
      if (sector) body.sector = sector;
      const { data } = await http.post("/admin/ops/recrawl-no-run", body);
      toast.success(`Enqueued ${data.count} recrawl(s)${sector ? ` in ${sector}` : ""}`);
      if (data.count > 0) {
        setRecrawlProgress({
          runIds: (data.enqueued || []).map((e) => e.run_id),
          done: 0,
          total: data.count,
          sector: sector || "all",
        });
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setRecrawlBusy(false); }
  };

  const [migPopup, setMigPopup] = useState(null);
  const runReadiness = async () => {
    setReadinessBusy(true);
    try {
      const { data } = await http.post("/admin/ops/readiness-check");
      setReadiness({ ...(readiness || {}), result: data.result });
      toast.success(`Readiness: ${data.result.status.toUpperCase()} (${data.result.issues.length} issue${data.result.issues.length === 1 ? "" : "s"})`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setReadinessBusy(false); }
  };
  const publishAccessRestricted = async (runId, brandName) => {
    if (!window.confirm(`Publish an ACCESS_RESTRICTED profile for ${brandName || "this brand"}?\n\nAll 7 categories will render as "Not assessable". The profile becomes publicly visible.`)) return;
    try {
      const { data } = await http.post(`/admin/pipeline/unreachable/${runId}/publish-access-restricted`);
      toast.success(`Published — ${data.attempted_urls_count} URL(s) recorded as attempted.`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);

  const runTestFetch = async () => {
    setTestBusy(true); setTestResult(null);
    try {
      const { data } = await http.post("/admin/ops/test-fetch", { url: testUrl });
      setTestResult(data);
    } catch (e) {
      setTestResult({ error: e.response?.data?.detail || e.message });
    } finally { setTestBusy(false); }
  };

  const retryDL = async (id) => { try { await http.post(`/admin/ops/dead-letter/${id}/retry`); toast.success("Re-queued"); load(); } catch { toast.error("Failed"); } };
  const releaseHeld = async (id) => { try { await http.post(`/admin/ops/held-updates/${id}/release`); toast.success("Released — re-queued"); load(); } catch { toast.error("Failed"); } };
  const addBlock = async (e) => {
    e.preventDefault();
    try { await http.post("/admin/pipeline/blocklist", newBlock); setNewBlock({ domain: "", reason: "" }); toast.success("Blocklisted"); load(); }
    catch { toast.error("Failed"); }
  };
  const rmBlock = async (dom) => { await http.delete(`/admin/pipeline/blocklist/${dom}`); load(); };
  const [bootstrapBusy, setBootstrapBusy] = useState(false);
  const enqueueUnbuilt = async (sector) => {
    setBootstrapBusy(true);
    try {
      const { data } = await http.post("/admin/pipeline/enqueue-unbuilt", null, { params: sector ? { sector } : {} });
      toast.success(`Enqueued ${data.enqueued.length} · Skipped ${data.skipped.length}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBootstrapBusy(false); }
  };
  const toggleCat = async (cat) => {
    const disabled = new Set(cfg.disabled_categories || []);
    if (disabled.has(cat)) disabled.delete(cat); else disabled.add(cat);
    await http.put("/admin/pipeline/config/disabled-categories", { disabled: [...disabled] });
    toast.success("Saved"); load();
  };

  if (!d || !cfg) return <AdminLayout><div className="bp-container py-24" /></AdminLayout>;

  return (
    <AdminLayout>
      <div className="bp-container py-8">
        <h1 className="font-serif text-4xl mb-6">Ops dashboard</h1>

        {/* Rate limiter — warning banner when 1h hits cross the alert threshold */}
        {rateLimit && rateLimit.ok && rateLimit.count_1h > (rateLimit.alert_threshold_1h ?? 100) && (
          <div
            className="mb-6 p-4 border-l-4 flex flex-col gap-2"
            style={{ borderColor: "#B45309", background: "#FFF7E0", color: "#8A5A2B" }}
            data-testid="rate-limit-warning-banner"
          >
            <div className="flex items-baseline justify-between">
              <div className="uppercase text-xs tracking-widest font-semibold">
                Rate limiter — elevated hit volume
              </div>
              <div className="text-xs" data-testid="rate-limit-counts">
                {rateLimit.count_1h} hits · 1h &nbsp;|&nbsp; {rateLimit.count_24h} hits · 24h
              </div>
            </div>
            <div className="text-sm">
              Someone is repeatedly hitting a rate-limited endpoint. Not necessarily malicious — could be a broken
              client — but worth a look. Threshold: {rateLimit.alert_threshold_1h ?? 100}/hour.
            </div>
            {(rateLimit.top_paths_1h || []).length > 0 && (
              <div className="text-xs" data-testid="rate-limit-top-paths">
                <span className="uppercase tracking-widest mr-2">Top paths (1h):</span>
                {rateLimit.top_paths_1h.map((p, i) => (
                  <span key={p.path} className="mr-3">
                    <code>{p.path}</code> ({p.hits})
                    {i < rateLimit.top_paths_1h.length - 1 ? "," : ""}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Console health — cross-list consistency snapshot + backfill migration counters (prod bug fix 2026-08-14) */}
        {(consoleHealth || migStatus) && (
          <div
            className="mb-6 p-5 bg-white border"
            style={{ borderColor: "var(--bp-border)" }}
            data-testid="console-health-panel"
          >
            <div className="flex items-baseline justify-between mb-3">
              <div className="uppercase text-xs tracking-widest" style={{ color: "var(--bp-text-muted)" }}>
                Console health · state unification
              </div>
              {migStatus?.console_state_backfill?.last_run_at && (
                <div className="text-xs" style={{ color: "var(--bp-text-muted)" }} data-testid="console-backfill-lastrun">
                  Backfill last run: {migStatus.console_state_backfill.last_run_at.slice(0, 19).replace("T", " ")}
                </div>
              )}
            </div>

            {consoleHealth && (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
                <Stat
                  label="Open runs"
                  value={Object.values(consoleHealth.open_pipeline_runs || {}).reduce((a, b) => a + b, 0)}
                  testId="console-open-runs"
                />
                <Stat
                  label="Awaiting approval"
                  value={consoleHealth.open_pipeline_runs?.AWAITING_APPROVAL || 0}
                  testId="console-awaiting"
                />
                <Stat
                  label="Needs attention"
                  value={consoleHealth.open_pipeline_runs?.NEEDS_ATTENTION || 0}
                  testId="console-needs-attention"
                />
                <Stat
                  label="Held"
                  value={consoleHealth.open_pipeline_runs?.HELD || 0}
                  testId="console-held"
                />
                <div
                  className="p-5 bg-white border"
                  style={{
                    borderColor: "var(--bp-border)",
                    background: (consoleHealth.totals?.brands_missing_last_checked || 0) > 0 ? "#FFF1F1" : "white",
                  }}
                  data-testid="console-missing-date"
                >
                  <div className="uppercase text-xs tracking-widest mb-2" style={{ color: "var(--bp-text-muted)" }}>
                    Brands · no date
                  </div>
                  <div
                    className="font-serif text-4xl"
                    style={{
                      color: (consoleHealth.totals?.brands_missing_last_checked || 0) > 0 ? "#B91C1C" : "var(--bp-text)",
                    }}
                  >
                    {consoleHealth.totals?.brands_missing_last_checked || 0}
                  </div>
                </div>
              </div>
            )}

            {migStatus?.console_state_backfill && (
              <div className="text-xs" style={{ color: "var(--bp-text-muted)" }}>
                <div className="mb-1">
                  <span className="uppercase tracking-widest mr-2">Last backfill wrote:</span>
                  <code data-testid="console-backfill-stats">
                    profiles={migStatus.console_state_backfill.stats?.profiles_review_state_healed ?? "n/a"} ·
                    &nbsp;runs={migStatus.console_state_backfill.stats?.runs_last_attempted_at_healed ?? "n/a"}
                  </code>
                </div>
                <div>
                  <span className="uppercase tracking-widest mr-2">Would heal now:</span>
                  <code data-testid="console-would-heal">
                    profiles={migStatus.console_state_backfill.would_heal_now?.profiles_missing_review_state ?? 0} ·
                    &nbsp;runs={migStatus.console_state_backfill.would_heal_now?.runs_missing_last_attempted_at ?? 0}
                  </code>
                  {(migStatus.console_state_backfill.would_heal_now?.profiles_missing_review_state === 0 &&
                    migStatus.console_state_backfill.would_heal_now?.runs_missing_last_attempted_at === 0) && (
                    <span className="ml-2" style={{ color: "#166534" }} data-testid="console-in-sync">
                      · in sync
                    </span>
                  )}
                </div>
              </div>
            )}

            {consoleHealth?.stuck_runs?.length > 0 && (
              <div
                className="mt-3 p-3 border-l-4 text-xs"
                style={{ borderColor: "#B91C1C", background: "#FFF1F1", color: "#B91C1C" }}
                data-testid="stuck-runs-alert"
              >
                <div className="uppercase tracking-widest font-semibold mb-1">
                  Stuck runs detected · {consoleHealth.totals?.stuck_runs || 0}
                </div>
                <div className="mb-2" style={{ color: "#8A2626" }}>
                  Pipeline_run in AWAITING_APPROVAL whose approval_task is already APPROVED. Should
                  always be zero — if this creeps up, the `_publish` path forgot to finalize its run.
                  Redeploy triggers a backfill that auto-heals them.
                </div>
                <ul className="list-disc pl-5 space-y-0.5" data-testid="stuck-runs-list">
                  {consoleHealth.stuck_runs.slice(0, 10).map((s) => (
                    <li key={s.run_id}>
                      <code>{s.brand_slug}</code>
                      <span style={{ color: "var(--bp-text-muted)" }}> · run {s.run_id?.slice(0, 8)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {consoleHealth?.brands_missing_last_checked?.length > 0 && (
              <div className="mt-3 text-xs" data-testid="console-missing-date-list" style={{ color: "#B91C1C" }}>
                <div className="flex items-baseline gap-3 flex-wrap mb-2">
                  <span className="uppercase tracking-widest">
                    Brands still without a last-checked date:
                  </span>
                  <Button
                    onClick={() => runRecrawlNoRun(null)}
                    disabled={recrawlBusy}
                    className="rounded-none h-7 text-xs px-3"
                    data-testid="recrawl-no-run-btn"
                  >
                    {recrawlBusy ? "Enqueueing…" : "Ricrawl all →"}
                  </Button>
                  <Button
                    onClick={() => runRecrawlNoRun("FASHION")}
                    disabled={recrawlBusy}
                    variant="outline"
                    className="rounded-none h-7 text-xs px-3"
                    data-testid="recrawl-fashion-btn"
                  >
                    Ricrawl FASHION →
                  </Button>
                  <Button
                    onClick={() => runRecrawlNoRun("FURNITURE")}
                    disabled={recrawlBusy}
                    variant="outline"
                    className="rounded-none h-7 text-xs px-3"
                    data-testid="recrawl-furniture-btn"
                  >
                    Ricrawl FURNITURE →
                  </Button>
                </div>
                {consoleHealth.brands_missing_last_checked.slice(0, 10).map((b, i) => (
                  <span key={b.slug} className="mr-3">
                    <code>{b.slug}</code>
                    <span style={{ color: "var(--bp-text-muted)" }}>
                      {" "}({b.has_run ? "run-without-terminal-date" : b.has_profile ? "profile-only" : "no-profile-no-run"})
                    </span>
                    {i < Math.min(consoleHealth.brands_missing_last_checked.length, 10) - 1 ? "," : ""}
                  </span>
                ))}
              </div>
            )}

            {/* Sparkline — healed docs across last N boots.
                Shows if the migration keeps finding docs to fix (regression signal) or stays at zero (healthy). */}
            {migLog?.entries?.length > 0 && (
              <div className="mt-4 pt-3 border-t" style={{ borderColor: "var(--bp-border)" }} data-testid="console-migration-sparkline">
                <div className="uppercase text-[10px] tracking-widest mb-2" style={{ color: "var(--bp-text-muted)" }}>
                  Backfill history · last {migLog.entries.length} boot{migLog.entries.length === 1 ? "" : "s"}
                </div>
                <div className="flex items-end gap-1" style={{ height: 40 }}>
                  {(() => {
                    const totals = migLog.entries.map((e) => {
                      const s = e.stats || {};
                      return (s.profiles_review_state_healed || 0) + (s.runs_last_attempted_at_healed || 0);
                    });
                    const max = Math.max(1, ...totals);
                    return migLog.entries.map((e, i) => {
                      const v = totals[i];
                      const h = Math.max(2, Math.round((v / max) * 36));
                      const accent = v > 0 ? "#B91C1C" : "#166534";
                      return (
                        <button
                          key={e.at + i}
                          type="button"
                          onClick={() => setMigPopup(e)}
                          className="flex-1 relative bg-transparent border-0 p-0 cursor-pointer"
                          title={`${e.at?.slice(0, 19).replace("T", " ")} — healed ${v} doc(s) · click for detail`}
                          style={{ minWidth: 6 }}
                          data-testid={`sparkline-bar-${i}`}
                        >
                          <div
                            style={{
                              height: h,
                              background: accent,
                              opacity: v > 0 ? 1 : 0.4,
                            }}
                          />
                        </button>
                      );
                    });
                  })()}
                </div>
                <div className="mt-1 flex justify-between text-[10px]" style={{ color: "var(--bp-text-muted)" }}>
                  <span>{migLog.entries[0]?.at?.slice(0, 10)}</span>
                  <span>Now</span>
                </div>
              </div>
            )}

            {/* Per-sector backlog progress bars — item 4. */}
            {sectorBacklog?.sectors?.length > 0 && (
              <div className="mt-4 pt-3 border-t" style={{ borderColor: "var(--bp-border)" }} data-testid="sector-backlog-widget">
                <div className="uppercase text-[10px] tracking-widest mb-3" style={{ color: "var(--bp-text-muted)" }}>
                  Sector backlog · brands crawled vs total
                </div>
                <div className="space-y-2">
                  {sectorBacklog.sectors.map((s) => {
                    const pct = s.total > 0 ? Math.round((s.published / s.total) * 100) : 0;
                    return (
                      <div key={s.code} data-testid={`sector-backlog-${s.code}`}>
                        <div className="flex items-baseline justify-between text-xs mb-1">
                          <span className="font-mono">{s.name || s.code}</span>
                          <span className="font-mono" style={{ color: "var(--bp-text-muted)" }}>
                            {s.published}/{s.total} crawled · {s.awaiting_approval} awaiting · {s.needs_attention} needs-attn · {s.no_run} no-run
                          </span>
                        </div>
                        <div className="h-2 w-full" style={{ background: "#EEE" }}>
                          <div
                            className="h-full"
                            style={{
                              width: `${pct}%`,
                              background: pct >= 75 ? "#166534" : pct >= 33 ? "#CA8A04" : "#B45309",
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Alert · brands whose crawler has no seed. Bug fix
                2026-08-17 — surfaces brands with missing / unparseable
                website_url so the operator can heal them BEFORE the
                pipeline goes empty. Only rendered when total > 0. */}
            {brandsNoWebsite?.total > 0 && (
              <div
                className="mt-4 pt-3 border-t"
                style={{ borderColor: "var(--bp-border)" }}
                data-testid="brands-without-website-alert"
              >
                <div className="flex items-baseline justify-between mb-3">
                  <div className="uppercase text-[10px] tracking-widest" style={{ color: "#B91C1C" }}>
                    ⚠︎ Brands without a crawlable website
                  </div>
                  <div className="font-mono text-xs" data-testid="brands-without-website-total" style={{ color: "#B91C1C" }}>
                    {brandsNoWebsite.total} total
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3 mb-3">
                  {[
                    { key: "missing", label: "No website", hint: "operator forgot to fill website_url" },
                    { key: "unparseable", label: "No scheme", hint: "website set but missing http(s)://" },
                    { key: "no_official_domains", label: "No domains", hint: "official_domains list is empty" },
                  ].map((b) => {
                    const bucket = brandsNoWebsite.buckets?.[b.key] || { count: 0, sample: [] };
                    return (
                      <div
                        key={b.key}
                        className="bg-white border p-2"
                        style={{ borderColor: "var(--bp-border)" }}
                        data-testid={`brands-without-website-bucket-${b.key}`}
                      >
                        <div className="uppercase text-[9px] tracking-widest" style={{ color: "var(--bp-text-muted)" }}>{b.label}</div>
                        <div className="font-mono text-lg" style={{ color: bucket.count > 0 ? "#B91C1C" : "var(--bp-text-muted)" }}>
                          {bucket.count}
                        </div>
                        <div className="text-[10px]" style={{ color: "var(--bp-text-muted)" }}>{b.hint}</div>
                      </div>
                    );
                  })}
                </div>
                {/* Union of all buckets, sample cap enforced server-side */}
                {(() => {
                  const rows = [
                    ...(brandsNoWebsite.buckets?.missing?.sample || []),
                    ...(brandsNoWebsite.buckets?.unparseable?.sample || []),
                    ...(brandsNoWebsite.buckets?.no_official_domains?.sample || []),
                  ].slice(0, 15);
                  if (!rows.length) return null;
                  return (
                    <div className="bg-white border" style={{ borderColor: "var(--bp-border)" }}>
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b text-left" style={{ borderColor: "var(--bp-border)" }}>
                            <th className="px-3 py-2 uppercase tracking-widest">Brand</th>
                            <th className="px-3 py-2 uppercase tracking-widest">Sector</th>
                            <th className="px-3 py-2 uppercase tracking-widest">Website (as stored)</th>
                            <th className="px-3 py-2 uppercase tracking-widest">Reason</th>
                            <th />
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((r) => (
                            <tr
                              key={`${r.reason}-${r.id}`}
                              className="border-b last:border-b-0"
                              style={{ borderColor: "var(--bp-border)" }}
                              data-testid={`brands-without-website-row-${r.slug}`}
                            >
                              <td className="px-3 py-2">
                                <div className="font-serif">{r.display_name}</div>
                                <div className="font-mono text-[10px]" style={{ color: "var(--bp-text-muted)" }}>{r.slug}</div>
                              </td>
                              <td className="px-3 py-2 font-mono" style={{ color: "var(--bp-text-muted)" }}>{r.sector_code}</td>
                              <td className="px-3 py-2 font-mono break-all" style={{ color: r.website_url ? "var(--bp-text-muted)" : "#B91C1C" }}>
                                {r.website_url || "— empty —"}
                              </td>
                              <td className="px-3 py-2 font-mono" style={{ color: "#B45309" }}>{r.reason}</td>
                              <td className="px-3 py-2 text-right">
                                <a href={`/admin/brands/${r.slug}`} className="bp-link">Fix →</a>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Discovery dry-run — pre-flight a domain without creating
                a brand. Runs the discovery lane against the given URL
                and shows kept / discarded / seed root health. */}
            <div
              className="mt-4 pt-3 border-t"
              style={{ borderColor: "var(--bp-border)" }}
              data-testid="discovery-dry-run"
            >
              <div className="flex items-baseline justify-between mb-2">
                <div className="uppercase text-[10px] tracking-widest" style={{ color: "var(--bp-text-muted)" }}>
                  Try discovery on this domain
                </div>
                <div className="text-[10px]" style={{ color: "var(--bp-text-muted)" }}>
                  read-only · doesn't create a brand
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="md:col-span-2 space-y-2">
                  <input
                    className="w-full border p-2 font-mono text-xs"
                    style={{ borderColor: "var(--bp-border)" }}
                    placeholder="e.g. numb-wear.it"
                    value={dryRun.website_url}
                    onChange={(e) => setDryRun({ ...dryRun, website_url: e.target.value })}
                    data-testid="dry-run-website"
                  />
                  <textarea
                    rows={3}
                    className="w-full border p-2 font-mono text-xs"
                    style={{ borderColor: "var(--bp-border)" }}
                    placeholder="Optional extra seed URLs (one per line)"
                    value={dryRun.seeds}
                    onChange={(e) => setDryRun({ ...dryRun, seeds: e.target.value })}
                    data-testid="dry-run-seeds"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <Button
                    onClick={runDryRun}
                    disabled={dryRunBusy}
                    className="rounded-none"
                    data-testid="dry-run-submit"
                  >
                    {dryRunBusy ? "Running…" : "Run discovery"}
                  </Button>
                  <div className="text-[10px]" style={{ color: "var(--bp-text-muted)" }}>
                    Hits the target site live (sitemap, robots, homepage).
                    Hard cap 60s. No LLM calls, no brand created.
                  </div>
                </div>
              </div>

              {dryRunResult && (
                <div className="mt-3 bg-white border p-3" style={{ borderColor: "var(--bp-border)" }}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="uppercase text-[10px] tracking-widest" style={{ color: "var(--bp-text-muted)" }}>
                      Result · {dryRunResult.website_url}
                    </div>
                    {/* Deep-link to AdminBrandsList with the verified URL + kept URLs
                        pre-loaded into the New brand popup so the operator can
                        onboard the brand in one click. */}
                    <button
                      onClick={() => {
                        const seeds = (dryRunResult.kept || []).map((k) => k.url).filter(Boolean);
                        const params = new URLSearchParams({
                          prefill_url: dryRunResult.website_url || "",
                          prefill_seeds: JSON.stringify(seeds),
                          open: "1",
                        });
                        nav(`/admin/brands?${params.toString()}`);
                      }}
                      className="text-xs bp-link"
                      data-testid="dry-run-save-as-brand"
                      title="Open the New brand popup pre-filled with this URL and the kept seed URLs"
                    >
                      Save this domain as new brand →
                    </button>
                  </div>
                  <div
                    className="grid grid-cols-2 md:grid-cols-6 gap-3 text-center"
                    data-testid="dry-run-summary"
                  >
                    {[
                      ["Kept", dryRunResult.kept_total || 0],
                      ["Discarded", dryRunResult.discarded_total || 0],
                      ["Candidates", dryRunResult.diagnostics?.candidates_found ?? 0],
                      ["After dedup", dryRunResult.diagnostics?.candidates_after_dedup ?? 0],
                      ["Sitemap URLs", dryRunResult.diagnostics?.sitemap_urls_found ?? 0],
                      ["Duration", `${dryRunResult.took_ms || 0}ms`],
                    ].map(([label, value]) => (
                      <div key={label}>
                        <div className="uppercase text-[9px] tracking-widest" style={{ color: "var(--bp-text-muted)" }}>{label}</div>
                        <div className="font-mono text-lg">{value}</div>
                      </div>
                    ))}
                  </div>

                  {dryRunResult.diagnostics?.no_domains && (
                    <div
                      className="mt-2 p-2 text-xs"
                      style={{ background: "#FEE2E2", color: "#B91C1C" }}
                      data-testid="dry-run-no-domains"
                    >
                      ⚠︎ Domain could not be parsed into a valid official_domains entry.
                    </div>
                  )}

                  {Object.keys(dryRunResult.diagnostics?.lanes_kept || {}).length > 0 && (
                    <div className="mt-3 pt-2 border-t text-xs" style={{ borderColor: "var(--bp-border)" }}>
                      <span className="uppercase tracking-widest text-[10px] mr-2" style={{ color: "var(--bp-text-muted)" }}>Lanes kept</span>
                      <span className="font-mono">
                        {Object.entries(dryRunResult.diagnostics.lanes_kept)
                          .map(([k, v]) => `${k}: ${v}`).join(" · ")}
                      </span>
                    </div>
                  )}

                  {(dryRunResult.diagnostics?.seed_roots || []).length > 0 && (
                    <div className="mt-3 pt-2 border-t text-xs" style={{ borderColor: "var(--bp-border)" }}>
                      <div className="uppercase tracking-widest text-[10px] mb-1" style={{ color: "var(--bp-text-muted)" }}>
                        Seed roots
                      </div>
                      <ul className="font-mono space-y-1">
                        {dryRunResult.diagnostics.seed_roots.map((s, i) => (
                          <li
                            key={`${s.url}-${i}`}
                            className="break-all"
                            data-testid={`dry-run-seed-${i}`}
                          >
                            <span style={{ color: s.blocked ? "#B91C1C" : "#166534" }}>
                              {s.blocked ? "✗" : "✓"}
                            </span>{" "}
                            {s.url}
                            <span className="ml-2" style={{ color: "var(--bp-text-muted)" }}>
                              {s.status ? `status ${s.status}` : ""}
                              {s.mode ? ` · ${s.mode}` : ""}
                              {s.reason ? ` · ${s.reason}` : ""}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {(dryRunResult.kept || []).length > 0 && (
                    <details className="mt-3 pt-2 border-t" style={{ borderColor: "var(--bp-border)" }}>
                      <summary className="cursor-pointer uppercase text-[10px] tracking-widest" style={{ color: "var(--bp-text-muted)" }}>
                        Kept URLs (first {dryRunResult.kept.length})
                      </summary>
                      <ul className="mt-2 text-xs font-mono space-y-1 max-h-64 overflow-auto">
                        {dryRunResult.kept.map((k, i) => (
                          <li key={`${k.url}-${i}`} className="break-all">
                            <span className="text-[10px] uppercase mr-1" style={{ color: "var(--bp-text-muted)" }}>{k.lane || "—"}</span>
                            <a href={k.url} target="_blank" rel="noopener noreferrer" className="bp-link">{k.url}</a>
                            {k.title && <span className="ml-2" style={{ color: "var(--bp-text-muted)" }}>· {k.title}</span>}
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}

                  {(dryRunResult.discarded || []).length > 0 && (
                    <details className="mt-3 pt-2 border-t" style={{ borderColor: "var(--bp-border)" }}>
                      <summary className="cursor-pointer uppercase text-[10px] tracking-widest" style={{ color: "var(--bp-text-muted)" }}>
                        Discarded URLs (first {dryRunResult.discarded.length})
                      </summary>
                      <ul className="mt-2 text-xs font-mono space-y-1 max-h-64 overflow-auto">
                        {dryRunResult.discarded.map((d, i) => (
                          <li key={`${d.url || "row"}-${i}`} className="break-all" style={{ color: "var(--bp-text-muted)" }}>
                            <span style={{ color: "#B45309" }}>{d.reason || "—"}</span>
                            {": "}
                            <span>{d.url || "—"}</span>
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
              )}

              {/* Recent dry-runs — last 20, LRU-capped server-side.
                  Click a row to re-populate the URL input above. */}
              {dryRunHistory.length > 0 && (
                <div
                  className="mt-3 border-t pt-3"
                  style={{ borderColor: "var(--bp-border)" }}
                  data-testid="dry-run-history"
                >
                  <div className="uppercase text-[10px] tracking-widest mb-2" style={{ color: "var(--bp-text-muted)" }}>
                    Recent dry-runs ({dryRunHistory.length})
                  </div>
                  <div className="bg-white border" style={{ borderColor: "var(--bp-border)" }}>
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b text-left" style={{ borderColor: "var(--bp-border)" }}>
                          <th className="px-3 py-2 uppercase tracking-widest">Website</th>
                          <th className="px-3 py-2 uppercase tracking-widest">Kept</th>
                          <th className="px-3 py-2 uppercase tracking-widest">Discarded</th>
                          <th className="px-3 py-2 uppercase tracking-widest">Duration</th>
                          <th className="px-3 py-2 uppercase tracking-widest">When</th>
                          <th className="px-3 py-2 uppercase tracking-widest">By</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dryRunHistory.map((r) => {
                          const when = r.ran_at ? new Date(r.ran_at) : null;
                          const keptColor = (r.kept_total || 0) === 0 ? "#B91C1C" : "#166534";
                          return (
                            <tr
                              key={r.id}
                              className="border-b last:border-b-0 hover:bg-gray-50 cursor-pointer"
                              style={{ borderColor: "var(--bp-border)" }}
                              onClick={() => applyHistoryRow(r)}
                              title="Copy to form above (does not re-run)"
                              data-testid={`dry-run-history-row-${r.id}`}
                            >
                              <td className="px-3 py-2 font-mono break-all max-w-xs">{r.website_url}</td>
                              <td className="px-3 py-2 font-mono" style={{ color: keptColor }}>{r.kept_total ?? 0}</td>
                              <td className="px-3 py-2 font-mono" style={{ color: "var(--bp-text-muted)" }}>{r.discarded_total ?? 0}</td>
                              <td className="px-3 py-2 font-mono" style={{ color: "var(--bp-text-muted)" }}>{r.took_ms ?? 0}ms</td>
                              <td className="px-3 py-2 font-mono" style={{ color: "var(--bp-text-muted)" }}>
                                {when ? when.toLocaleString() : "—"}
                              </td>
                              <td className="px-3 py-2 font-mono" style={{ color: "var(--bp-text-muted)" }}>
                                {r.editor_email || "—"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            <Dialog open={!!migPopup} onOpenChange={(v) => !v && setMigPopup(null)}>
              <DialogContent data-testid="migration-boot-popup" className="max-w-lg">
                <DialogHeader>
                  <DialogTitle className="font-serif">
                    Boot · {migPopup?.at?.slice(0, 19).replace("T", " ")}
                  </DialogTitle>
                </DialogHeader>
                {migPopup && (
                  <div className="text-sm space-y-3">
                    <div>
                      <div className="uppercase text-[10px] tracking-widest" style={{ color: "var(--bp-text-muted)" }}>
                        Healed on this boot
                      </div>
                      <div className="font-mono">
                        profiles = {migPopup.stats?.profiles_review_state_healed ?? 0} ·
                        &nbsp;runs = {migPopup.stats?.runs_last_attempted_at_healed ?? 0}
                      </div>
                    </div>
                    {migPopup.stats?.profiles_healed_sample?.length > 0 && (
                      <div>
                        <div className="uppercase text-[10px] tracking-widest mb-1" style={{ color: "var(--bp-text-muted)" }}>
                          Profile IDs (first {migPopup.stats.profiles_healed_sample.length})
                        </div>
                        <ul className="font-mono text-xs list-disc pl-5 space-y-0.5">
                          {migPopup.stats.profiles_healed_sample.map((bid) => (
                            <li key={bid}><code>{bid}</code></li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {migPopup.stats?.runs_healed_sample?.length > 0 && (
                      <div>
                        <div className="uppercase text-[10px] tracking-widest mb-1" style={{ color: "var(--bp-text-muted)" }}>
                          Run IDs (first {migPopup.stats.runs_healed_sample.length})
                        </div>
                        <ul className="font-mono text-xs list-disc pl-5 space-y-0.5">
                          {migPopup.stats.runs_healed_sample.map((rid) => (
                            <li key={rid}><code>{rid}</code></li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {!migPopup.stats?.profiles_healed_sample?.length && !migPopup.stats?.runs_healed_sample?.length && (
                      <div style={{ color: "var(--bp-text-muted)" }} className="text-xs">
                        No documents needed healing on this boot — everything was in sync.
                      </div>
                    )}
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </div>
        )}

        {/* Coverage-by-lane header — kept below console health so a broken list state is visible first */}
        {coverageErr && !coverage && (
          <div className="mb-6 p-3 border text-xs" style={{ borderColor: "var(--bp-border)", background: "#FFF7E0", color: "#8A5A2B" }} data-testid="coverage-by-lane-error">
            Coverage-by-lane endpoint unavailable — retrying every 15s.
          </div>
        )}
        {coverage && (
          <div className="mb-6 p-5 bg-white border" style={{ borderColor: "var(--bp-border)" }} data-testid="coverage-by-lane-panel">
            <div className="flex items-baseline justify-between mb-3">
              <div className="uppercase text-xs tracking-widest" style={{ color: "var(--bp-text-muted)" }}>
                Coverage by lane · last {coverage.window_hours}h
              </div>
              <div className="text-xs" style={{ color: "var(--bp-text-muted)" }} data-testid="coverage-total">
                {coverage.total_snapshots} snapshot{coverage.total_snapshots === 1 ? "" : "s"} · <span style={{ color: coverage.lift.count ? "var(--bp-accent)" : "var(--bp-text-muted)" }} data-testid="coverage-lift">
                  {coverage.lift.count} rescued ({coverage.lift.pct_of_total}%)
                </span>
              </div>
            </div>

            {coverage.total_snapshots === 0 ? (
              <div className="text-sm" style={{ color: "var(--bp-text-muted)" }} data-testid="coverage-empty">
                No fetches in the window. Kick off a bulk intake or reprocess a brand to populate this widget.
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
                  <LaneStat label="Direct" value={coverage.buckets.direct} tone="neutral" testId="coverage-direct" />
                  <LaneStat label="Fixed proxy" value={coverage.buckets.fixed_proxy} tone="neutral" testId="coverage-fixed-proxy" hideWhenZero />
                  <LaneStat label="URL variant" value={coverage.buckets.variant} tone="rescue" testId="coverage-variant" />
                  <LaneStat label="Wayback" value={coverage.buckets.wayback} tone="rescue" testId="coverage-wayback" />
                  <LaneStat label="archive.today" value={coverage.buckets.archive_today} tone="rescue" testId="coverage-archive-today" />
                  <LaneStat label="Paid" value={coverage.buckets.paid_fallback} tone="paid" testId="coverage-paid" />
                  <LaneStat label="Other" value={coverage.buckets.other} tone="neutral" testId="coverage-other" hideWhenZero />
                </div>
                {coverage.buckets.paid_fallback > 0 && Object.keys(coverage.paid_breakdown).length > 0 && (
                  <div className="mt-3 text-xs" style={{ color: "var(--bp-text-muted)" }} data-testid="coverage-paid-breakdown">
                    Paid providers:&nbsp;
                    {Object.entries(coverage.paid_breakdown).map(([p, n], i, a) => (
                      <span key={p}>{p} · {n}{i < a.length - 1 ? ",  " : ""}</span>
                    ))}
                  </div>
                )}
                {coverage.variant_examples.length > 0 && (
                  <div className="mt-2 text-xs truncate" style={{ color: "var(--bp-text-muted)" }} title={coverage.variant_examples.join("\n")}>
                    Variant examples: {coverage.variant_examples.slice(0, 2).join(" · ")}{coverage.variant_examples.length > 2 ? " …" : ""}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Compose path — LLM vs template fallback ratio (silent degradation monitor) */}
        {composePathErr && !composePath && (
          <div className="mb-6 p-3 border text-xs" style={{ borderColor: "var(--bp-border)", background: "#FFF7E0", color: "#8A5A2B" }} data-testid="compose-path-error">
            Compose-path-stats endpoint unavailable — retrying every 15s.
          </div>
        )}
        {composePath && (
          <div className="mb-6 p-5 bg-white border" style={{ borderColor: "var(--bp-border)" }} data-testid="compose-path-panel">
            <div className="flex items-baseline justify-between mb-3">
              <div className="uppercase text-xs tracking-widest" style={{ color: "var(--bp-text-muted)" }}>
                Compose path · last {composePath.days} day{composePath.days === 1 ? "" : "s"}
              </div>
              <div className="text-xs" style={{ color: "var(--bp-text-muted)" }} data-testid="compose-path-totals">
                {(composePath.totals?.llm || 0) + (composePath.totals?.template || 0) + (composePath.totals?.unknown || 0)} composed runs
              </div>
            </div>
            <div className="flex items-baseline gap-6 flex-wrap">
              <div>
                <div
                  className="font-mono text-3xl"
                  style={{ color: (composePath.template_fallback_pct || 0) >= 30 ? "#B91C1C" : (composePath.template_fallback_pct || 0) >= 10 ? "#B45309" : "var(--bp-text)" }}
                  data-testid="compose-template-pct"
                >
                  {(composePath.template_fallback_pct || 0).toFixed(1)}%
                </div>
                <div className="text-xs uppercase tracking-widest" style={{ color: "var(--bp-text-muted)" }}>
                  Template fallback
                </div>
              </div>
              {composePath.it_totals && (
                <div>
                  <div
                    className="font-mono text-3xl"
                    style={{ color: (composePath.it_missing_pct || 0) >= 30 ? "#B91C1C" : (composePath.it_missing_pct || 0) >= 10 ? "#B45309" : "var(--bp-text)" }}
                    data-testid="compose-it-missing-pct"
                  >
                    {(composePath.it_missing_pct || 0).toFixed(1)}%
                  </div>
                  <div className="text-xs uppercase tracking-widest" style={{ color: "var(--bp-text-muted)" }}>
                    IT slot missing
                  </div>
                </div>
              )}
              <div className="text-sm" style={{ color: "var(--bp-text-muted)" }}>
                <div>
                  <span style={{ color: "var(--bp-text)" }}>LLM:</span> {composePath.totals?.llm || 0}
                  &nbsp;·&nbsp;
                  <span style={{ color: "var(--bp-text)" }}>Template:</span> {composePath.totals?.template || 0}
                  {(composePath.totals?.unknown || 0) > 0 && (
                    <>&nbsp;·&nbsp;<span style={{ color: "var(--bp-text)" }}>Unknown:</span> {composePath.totals.unknown}</>
                  )}
                </div>
                {composePath.by_day && composePath.by_day.length > 0 && composePath.by_day[0].avg_summary_len && (
                  <div className="mt-1">
                    Avg summary length (today):&nbsp;
                    LLM {(composePath.by_day[composePath.by_day.length - 1].avg_summary_len?.llm ?? "—")}
                    &nbsp;·&nbsp;
                    Template {(composePath.by_day[composePath.by_day.length - 1].avg_summary_len?.template ?? "—")}
                  </div>
                )}
              </div>
            </div>
            {composePath.by_day && composePath.by_day.some((d) => (d.errors || []).length > 0 || (d.it_errors || []).length > 0) && (
              <div className="mt-4 grid gap-4 md:grid-cols-2">
                {composePath.by_day.some((d) => (d.errors || []).length > 0) && (
                  <div>
                    <div className="uppercase text-xs tracking-widest mb-2" style={{ color: "var(--bp-text-muted)" }}>
                      Top EN fallback reasons
                    </div>
                    <ul className="text-sm space-y-1" data-testid="compose-path-errors">
                      {(() => {
                        const counts = {};
                        composePath.by_day.forEach((d) => (d.errors || []).forEach((e) => { counts[e] = (counts[e] || 0) + 1; }));
                        return Object.entries(counts)
                          .sort((a, b) => b[1] - a[1])
                          .slice(0, 5)
                          .map(([reason, n]) => (
                            <li key={reason} className="font-mono text-xs">
                              <span style={{ color: "var(--bp-text-muted)" }}>×{n}</span>&nbsp;&nbsp;{reason}
                            </li>
                          ));
                      })()}
                    </ul>
                  </div>
                )}
                {composePath.by_day.some((d) => (d.it_errors || []).length > 0) && (
                  <div>
                    <div className="uppercase text-xs tracking-widest mb-2" style={{ color: "var(--bp-text-muted)" }}>
                      Top IT rejection reasons
                    </div>
                    <ul className="text-sm space-y-1" data-testid="compose-it-errors">
                      {(() => {
                        const counts = {};
                        composePath.by_day.forEach((d) => (d.it_errors || []).forEach((e) => { counts[e] = (counts[e] || 0) + 1; }));
                        return Object.entries(counts)
                          .sort((a, b) => b[1] - a[1])
                          .slice(0, 5)
                          .map(([reason, n]) => (
                            <li key={reason} className="font-mono text-xs">
                              <span style={{ color: "var(--bp-text-muted)" }}>×{n}</span>&nbsp;&nbsp;{reason}
                            </li>
                          ));
                      })()}
                    </ul>
                  </div>
                )}
              </div>
            )}
            {composePath.by_day && composePath.by_day.length > 1 && (
              <div className="mt-4">
                <div className="uppercase text-xs tracking-widest mb-2" style={{ color: "var(--bp-text-muted)" }}>
                  Daily trend
                </div>
                <table className="w-full text-xs" data-testid="compose-path-trend">
                  <thead>
                    <tr style={{ color: "var(--bp-text-muted)" }}>
                      <th className="text-left font-normal">Day</th>
                      <th className="text-right font-normal">LLM</th>
                      <th className="text-right font-normal">Template</th>
                      <th className="text-right font-normal">Template%</th>
                      <th className="text-right font-normal">IT ok</th>
                      <th className="text-right font-normal">IT miss</th>
                      <th className="text-right font-normal">IT miss%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {composePath.by_day.map((row) => (
                      <tr key={row.day} className="font-mono border-t" style={{ borderColor: "var(--bp-border)" }}>
                        <td className="py-1">{row.day}</td>
                        <td className="py-1 text-right">{row.llm}</td>
                        <td className="py-1 text-right">{row.template}</td>
                        <td className="py-1 text-right" style={{ color: row.template_pct >= 30 ? "#B91C1C" : row.template_pct >= 10 ? "#B45309" : "var(--bp-text)" }}>
                          {row.template_pct.toFixed(1)}%
                        </td>
                        <td className="py-1 text-right">{row.it_success ?? 0}</td>
                        <td className="py-1 text-right">{row.it_missing ?? 0}</td>
                        <td className="py-1 text-right" style={{ color: (row.it_missing_pct || 0) >= 30 ? "#B91C1C" : (row.it_missing_pct || 0) >= 10 ? "#B45309" : "var(--bp-text)" }}>
                          {(row.it_missing_pct ?? 0).toFixed(1)}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Recent bulk-intake batches (last 7 days) */}
        {recentBatches && recentBatches.batches.length > 0 && (
          <div className="mb-6 p-5 bg-white border" style={{ borderColor: "var(--bp-border)" }} data-testid="recent-bulk-batches-panel">
            <div className="flex items-center justify-between mb-3">
              <div className="uppercase text-xs tracking-widest" style={{ color: "var(--bp-text-muted)" }}>
                Recent bulk batches (last {recentBatches.days} days)
              </div>
              <a href="/admin/bulk-intake" className="text-xs bp-link" data-testid="recent-batches-all-link">All batches →</a>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b" style={{ borderColor: "var(--bp-border)" }}>
                  <th className="text-left py-1 font-normal text-xs uppercase tracking-widest" style={{ color: "var(--bp-text-muted)" }}>Submitted</th>
                  <th className="text-right py-1 font-normal text-xs uppercase tracking-widest" style={{ color: "var(--bp-text-muted)" }}>Brands</th>
                  <th className="text-left py-1 font-normal text-xs uppercase tracking-widest pl-4" style={{ color: "var(--bp-text-muted)" }}>Status</th>
                  <th className="text-left py-1 font-normal text-xs uppercase tracking-widest pl-4" style={{ color: "var(--bp-text-muted)" }}>Run states</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {recentBatches.batches.map((b) => (
                  <tr key={b.id} className="border-b" style={{ borderColor: "var(--bp-border)" }} data-testid={`recent-batch-${b.id}`}>
                    <td className="py-2 whitespace-nowrap">
                      <span title={b.submitted_at}>{relativeTime(b.submitted_at)}</span>
                    </td>
                    <td className="py-2 text-right font-mono">{b.row_count_valid}</td>
                    <td className="py-2 pl-4">
                      {b.completed ? (
                        <span className="inline-block px-2 py-0.5 text-xs bg-emerald-100 text-emerald-800 uppercase tracking-widest" data-testid={`recent-batch-status-${b.id}`}>
                          Completed
                          {b.completed_notified_at ? " · notified" : ""}
                        </span>
                      ) : (
                        <span className="inline-block px-2 py-0.5 text-xs bg-blue-100 text-blue-800 uppercase tracking-widest" data-testid={`recent-batch-status-${b.id}`}>
                          Running · {b.pending_runs}/{b.total_runs || b.row_count_valid} pending
                        </span>
                      )}
                    </td>
                    <td className="py-2 pl-4">
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(b.state_counts).length === 0 ? (
                          <span className="text-xs" style={{ color: "var(--bp-text-muted)" }}>—</span>
                        ) : (
                          Object.entries(b.state_counts)
                            .sort((a, c) => c[1] - a[1])
                            .map(([state, count]) => (
                              <span
                                key={state}
                                className={`inline-block px-2 py-0.5 text-xs ${STATE_CHIP_COLOR[state] || STATE_CHIP_COLOR.other}`}
                                title={state}
                              >
                                {state.replace("PUBLISHED_ACCESS_RESTRICTED", "PUB_RESTRICTED")}: {count}
                              </span>
                            ))
                        )}
                      </div>
                    </td>
                    <td className="py-2 pl-4 text-right">
                      <a href={`/admin/bulk-intake/${b.id}`} className="text-xs bp-link" data-testid={`recent-batch-open-${b.id}`}>Open →</a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-3 text-xs" style={{ color: "var(--bp-text-muted)" }}>
              Backup view of the bulk-intake email digest. When RESEND_API_KEY is configured, admins are also emailed a summary the moment a batch finishes.
            </div>
          </div>
        )}

        {/* Data health — sectors, brands, migrations */}
        {health && (
          <div className="mb-6 p-5 bg-white border" style={{ borderColor: "var(--bp-border)" }} data-testid="data-health-panel">
            <div className="uppercase text-xs tracking-widest mb-3" style={{ color: "var(--bp-text-muted)" }}>Data health</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="text-xs uppercase tracking-widest mb-2" style={{ color: "var(--bp-text-muted)" }}>Sectors &amp; brands</div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b" style={{ borderColor: "var(--bp-border)" }}>
                      <th className="text-left py-1 font-normal text-xs uppercase tracking-widest" style={{ color: "var(--bp-text-muted)" }}>Sector</th>
                      <th className="text-right py-1 font-normal text-xs uppercase tracking-widest" style={{ color: "var(--bp-text-muted)" }}>Total</th>
                      <th className="text-right py-1 font-normal text-xs uppercase tracking-widest" style={{ color: "var(--bp-text-muted)" }}>With baseline</th>
                      <th className="text-right py-1 font-normal text-xs uppercase tracking-widest" style={{ color: "var(--bp-text-muted)" }}>Unbuilt</th>
                      <th className="text-right py-1 font-normal text-xs uppercase tracking-widest" style={{ color: "var(--bp-text-muted)" }}>In-flight</th>
                      <th className="text-right py-1 font-normal text-xs uppercase tracking-widest" style={{ color: "var(--bp-text-muted)" }}>Never run</th>
                    </tr>
                  </thead>
                  <tbody>
                    {health.sectors.map((s) => (
                      <tr key={s.code} className="border-b" style={{ borderColor: "var(--bp-border)" }} data-testid={`health-sector-${s.code.toLowerCase()}`}>
                        <td className="py-2 font-serif">{s.name_en} <span className="text-xs font-mono" style={{ color: "var(--bp-text-muted)" }}>{s.code}</span></td>
                        <td className="py-2 text-right font-mono">{s.total_brands}</td>
                        <td className="py-2 text-right font-mono">{s.with_baseline}</td>
                        <td className="py-2 text-right font-mono">{s.unbuilt}</td>
                        <td className="py-2 text-right font-mono">{s.in_flight_runs}</td>
                        <td className={`py-2 text-right font-mono ${s.never_started > 0 ? "text-amber-700" : ""}`}>{s.never_started}</td>
                      </tr>
                    ))}
                    {health.sectors.length === 0 && (
                      <tr><td colSpan={6} className="py-4 text-center text-sm" style={{ color: "var(--bp-text-muted)" }}>No sectors present.</td></tr>
                    )}
                  </tbody>
                </table>
                <div className="mt-2 text-xs" style={{ color: "var(--bp-text-muted)" }}>
                  Totals — sectors: <span className="font-mono">{health.totals.sectors}</span> · brands: <span className="font-mono">{health.totals.brands}</span> · public: <span className="font-mono">{health.totals.with_baseline}</span>
                </div>
                {health.fetch_efficiency_7d && (
                  <div className="mt-4 pt-3 border-t" style={{ borderColor: "var(--bp-border)" }} data-testid="fetch-efficiency-panel">
                    <div className="text-xs uppercase tracking-widest mb-2" style={{ color: "var(--bp-text-muted)" }}>Fetch efficiency (last 7 days)</div>
                    {health.fetch_efficiency_7d.total_fetches === 0 ? (
                      <div className="text-xs" style={{ color: "var(--bp-text-muted)" }}>No fetches in the last 7 days.</div>
                    ) : (
                      <div className="grid grid-cols-3 gap-3 text-sm">
                        <div>
                          <div className="text-xs uppercase tracking-widest" style={{ color: "var(--bp-text-muted)" }}>Total fetches</div>
                          <div className="font-serif text-2xl" data-testid="metric-total-fetches">{health.fetch_efficiency_7d.total_fetches}</div>
                        </div>
                        <div>
                          <div className="text-xs uppercase tracking-widest" style={{ color: "var(--bp-text-muted)" }}>Cache hits (304)</div>
                          <div className="font-serif text-2xl" data-testid="metric-cache-hits">
                            {health.fetch_efficiency_7d.conditional_304_hits}
                            <span className="text-xs font-mono ml-2" style={{ color: (health.fetch_efficiency_7d.cache_hit_ratio ?? 0) > 0.3 ? "#0F7B3D" : "var(--bp-text-muted)" }}>
                              {health.fetch_efficiency_7d.cache_hit_ratio !== null ? `${Math.round(health.fetch_efficiency_7d.cache_hit_ratio * 100)}%` : "—"}
                            </span>
                          </div>
                        </div>
                        <div>
                          <div className="text-xs uppercase tracking-widest" style={{ color: "var(--bp-text-muted)" }}>Playwright</div>
                          <div className="font-serif text-2xl" data-testid="metric-playwright">
                            {health.fetch_efficiency_7d.playwright_renders}
                            <span className="text-xs font-mono ml-2" style={{ color: (health.fetch_efficiency_7d.playwright_ratio ?? 0) > 0.5 ? "#B00020" : "var(--bp-text-muted)" }}>
                              {health.fetch_efficiency_7d.playwright_ratio !== null ? `${Math.round(health.fetch_efficiency_7d.playwright_ratio * 100)}%` : "—"}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="mt-2 text-xs" style={{ color: "var(--bp-text-muted)" }}>
                      Higher cache-hit ratio = lower LLM cost on refresh runs. High playwright ratio may signal bot-wall pressure.
                    </div>
                  </div>
                )}
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest mb-2" style={{ color: "var(--bp-text-muted)" }}>Startup migrations</div>
                <ul className="text-xs font-mono space-y-1" data-testid="migration-history">
                  {health.migrations.map((m) => (
                    <li key={m.name} className="flex justify-between gap-4 border-b py-1" style={{ borderColor: "var(--bp-border)" }} data-testid={`migration-row-${m.name}`}>
                      <span className={m.status === "failed" ? "text-red-700 font-bold" : ""}>{m.name}</span>
                      <span style={{ color: "var(--bp-text-muted)" }}>
                        {m.last_run_at?.slice(0, 19).replace("T", " ")}
                        {" · "}{m.duration_ms}ms
                        {" · "}<span className={m.status === "failed" ? "text-red-700" : "text-green-700"}>{m.status}</span>
                      </span>
                    </li>
                  ))}
                  {(!health.migrations || health.migrations.length === 0) && (
                    <li style={{ color: "var(--bp-text-muted)" }}>No migrations recorded yet — restart backend to populate.</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Deployment readiness */}
        {readiness && readiness.result && (() => {
          const r = readiness.result;
          const bg = r.status === "ok" ? "#E6F4EA" : r.status === "warn" ? "#FFF8E1" : "#FDECEA";
          const border = r.status === "ok" ? "#0F7B3D" : r.status === "warn" ? "#B97700" : "#B00020";
          const label = { ok: "PASS", warn: "WARN", fail: "FAIL" }[r.status];
          return (
            <div className="mb-6 p-5 border" style={{ borderColor: border, background: bg }} data-testid="readiness-panel">
              <div className="flex items-start justify-between gap-4 mb-3">
                <div>
                  <div className="uppercase text-xs tracking-widest" style={{ color: "var(--bp-text-muted)" }}>Deployment readiness</div>
                  <div className="mt-1 flex items-baseline gap-3">
                    <span className="font-serif text-2xl" data-testid="readiness-status" style={{ color: border }}>{label}</span>
                    <span className="text-xs font-mono" style={{ color: "var(--bp-text-muted)" }}>Last checked: {r.checked_at?.slice(0, 19).replace("T", " ")}</span>
                  </div>
                </div>
                <Button onClick={runReadiness} disabled={readinessBusy} data-testid="readiness-run" className="rounded-none">
                  {readinessBusy ? "…" : "Re-run check"}
                </Button>
              </div>
              {r.issues.length === 0 ? (
                <div className="text-sm" data-testid="readiness-empty">All structural checks pass — no action needed.</div>
              ) : (
                <ul className="text-sm space-y-1" data-testid="readiness-issues">
                  {r.issues.map((i, idx) => (
                    <li key={`${i.code}-${i.sector || ""}-${idx}`} className="flex items-start gap-2" data-testid={`readiness-issue-${i.code}`}>
                      <span className={`font-mono text-xs uppercase px-1.5 py-0.5 ${i.severity === "critical" ? "bg-red-700 text-white" : "bg-amber-200"}`}>{i.severity}</span>
                      {i.sector && <span className="font-mono text-xs">{i.sector}</span>}
                      <span>{i.message}</span>
                    </li>
                  ))}
                </ul>
              )}
              <div className="mt-3 text-xs" style={{ color: "var(--bp-text-muted)" }}>
                Notifications:{" "}
                <span className="font-mono">webhook {readiness.webhook_configured ? "ON" : "off"}</span>{" · "}
                <span className="font-mono">email {readiness.email_configured ? "ON" : "off"}</span>{" · "}
                <span>Set env vars <code>DEPLOY_ALERT_WEBHOOK_URL</code> (Slack/Discord-compatible) and/or <code>DEPLOY_ALERT_EMAIL</code> (requires RESEND_API_KEY) to enable.</span>
              </div>
            </div>
          );
        })()}

        {/* Bootstrap: enqueue brands with no baseline yet */}
        <div className="mb-6 p-4 bg-white border flex flex-wrap items-center justify-between gap-3" style={{ borderColor: "var(--bp-border)" }} data-testid="bootstrap-panel">
          <div className="text-sm">
            <div className="uppercase text-xs tracking-widest mb-1" style={{ color: "var(--bp-text-muted)" }}>Bootstrap</div>
            <div>Enqueue a first pipeline run for every brand that has no baseline yet (safe to click twice — in-flight runs are skipped).</div>
          </div>
          <div className="flex gap-2">
            <Button disabled={bootstrapBusy} onClick={() => enqueueUnbuilt("FURNITURE")} data-testid="enqueue-furniture" className="rounded-none">
              {bootstrapBusy ? "…" : "Enqueue unbuilt Furniture"}
            </Button>
            <Button disabled={bootstrapBusy} onClick={() => enqueueUnbuilt("FASHION")} data-testid="enqueue-fashion" variant="outline" className="rounded-none">
              {bootstrapBusy ? "…" : "Enqueue unbuilt Fashion"}
            </Button>
            <Button disabled={bootstrapBusy} onClick={() => enqueueUnbuilt(null)} data-testid="enqueue-all" variant="outline" className="rounded-none">
              {bootstrapBusy ? "…" : "All sectors"}
            </Button>
          </div>
        </div>

        {/* Diagnostics panel */}
        {worker && (
          <div className="mb-8 p-5 bg-white border" style={{ borderColor: worker.worker_alive ? "var(--bp-border)" : "#B97700", background: worker.worker_alive ? "#fff" : "#FFF8E1" }} data-testid="diagnostics-panel">
            <div className="uppercase text-xs tracking-widest mb-3" style={{ color: "var(--bp-text-muted)" }}>Pipeline diagnostics</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <div className="text-xs uppercase tracking-widest" style={{ color: "var(--bp-text-muted)" }}>Worker</div>
                <div className={`font-mono ${worker.worker_alive ? "text-green-700" : "text-amber-700 font-bold"}`} data-testid="worker-alive">
                  {worker.worker_alive ? "ALIVE" : "NOT RESPONDING"}
                </div>
                <div className="text-xs font-mono" style={{ color: "var(--bp-text-muted)" }}>Last heartbeat: {worker.last_heartbeat_at ? `${worker.last_heartbeat_age_seconds}s ago` : "never"}</div>
                <div className="text-xs font-mono" style={{ color: "var(--bp-text-muted)" }}>Queued: {worker.queue?.queued ?? "—"} · Running: {worker.queue?.running ?? "—"}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest" style={{ color: "var(--bp-text-muted)" }}>Environment</div>
                <ul className="text-xs font-mono space-y-0.5" data-testid="env-list">
                  <li className={worker.env?.EMERGENT_LLM_KEY_present ? "text-green-700" : "text-red-700 font-bold"}>EMERGENT_LLM_KEY: {worker.env?.EMERGENT_LLM_KEY_present ? "set" : "MISSING"}</li>
                  <li>MONGO_URL: {worker.env?.MONGO_URL_present ? "set" : "MISSING"}</li>
                  <li>RESEND_API_KEY: {worker.env?.RESEND_API_KEY_present ? "set" : "not set (email mocked)"}</li>
                  <li className="truncate">Extract model: {worker.env?.PIPELINE_LLM_MODEL_EXTRACT || "—"}</li>
                  <li className="truncate">Classify model: {worker.env?.PIPELINE_LLM_MODEL_CLASSIFY || "—"}</li>
                  <li className="truncate">User-agent: {(worker.env?.PIPELINE_USER_AGENT || "browser+bot suffix (default)").slice(0, 60)}</li>
                  <li>Playwright fallback: {worker.env?.PIPELINE_PLAYWRIGHT_ENABLED === "0" ? "OFF" : "ON"}{worker.env?.PLAYWRIGHT_BROWSERS_PATH ? ` (browsers @ ${worker.env.PLAYWRIGHT_BROWSERS_PATH})` : ""}</li>
                </ul>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest mb-1" style={{ color: "var(--bp-text-muted)" }}>Test fetch</div>
                <div className="flex gap-1 mb-2">
                  <Input value={testUrl} onChange={(e) => setTestUrl(e.target.value)} className="text-xs" data-testid="test-fetch-url" />
                  <Button onClick={runTestFetch} disabled={testBusy} className="rounded-none text-xs" data-testid="test-fetch-submit">{testBusy ? "…" : "Run"}</Button>
                </div>
                {testResult && (
                  <div>
                    <div className="flex gap-3 text-xs font-mono mb-1" data-testid="test-fetch-summary">
                      <span>mode: <b className={testResult.fetch_mode === "playwright" ? "text-blue-700" : "text-green-700"}>{testResult.fetch_mode || "—"}</b></span>
                      <span>blocked: <b className={testResult.blocked ? "text-amber-700" : ""}>{String(!!testResult.blocked)}</b></span>
                      <span>text: <b>{testResult.text_length ?? 0} chars</b></span>
                    </div>
                    <pre className="text-[10px] font-mono bg-neutral-100 p-2 max-h-40 overflow-auto" data-testid="test-fetch-result">{JSON.stringify(testResult, null, 2)}</pre>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          <Stat label="Awaiting first approval" value={d.counts.awaiting_first_approval} testId="ops-awaiting" />
          <Stat label="Published" value={d.counts.published} testId="ops-published" />
          <Stat label="Held (anomaly)" value={d.counts.held_by_anomaly_guard} testId="ops-held" />
          <Stat label="Unreachable" value={d.counts.needs_attention ?? 0} testId="ops-needs-attention" />
          <Stat label="Stale (>90d)" value={d.counts.stale} testId="ops-stale" />
          <Stat label="Failed runs" value={d.counts.failed} testId="ops-failed" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
          <div className="p-5 bg-white border" style={{ borderColor: "var(--bp-border)" }}>
            <div className="uppercase text-xs tracking-widest mb-3" style={{ color: "var(--bp-text-muted)" }}>Per-category accuracy (from human corrections)</div>
            <ul className="space-y-1 text-sm">
              {CATS.map((c) => (
                <li key={c} className="flex justify-between">
                  <span>{c}</span>
                  <span className="font-mono">{d.accuracy_by_category[c] == null ? "—" : `${d.accuracy_by_category[c]}%`}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="p-5 bg-white border" style={{ borderColor: "var(--bp-border)" }} data-testid="ops-cost">
            <div className="uppercase text-xs tracking-widest mb-3" style={{ color: "var(--bp-text-muted)" }}>Cost (recent 50 successful runs)</div>
            <div className="text-sm">Avg LLM calls / profile: <span className="font-mono">{d.avg_llm_calls_per_profile}</span></div>
            <div className="text-sm">Avg tokens / profile: <span className="font-mono">{d.avg_tokens_per_profile}</span></div>
          </div>
        </div>

        {/* Unreachable brands — coverage-gate failures */}
        <h2 className="font-serif text-2xl mt-10 mb-3">Unreachable — needs attention</h2>
        <p className="text-xs mb-3" style={{ color: "var(--bp-text-muted)" }}>Runs that read fewer than 3 usable pages. Statuses/summary were not produced; any previously-published profile stays live untouched.</p>
        <div className="bg-white border" style={{ borderColor: "var(--bp-border)" }} data-testid="unreachable-table">
          {(d.unreachable || []).length === 0 && (
            <div className="px-4 py-4 text-center text-sm" style={{ color: "var(--bp-text-muted)" }}>No unreachable brands.</div>
          )}
          {(d.unreachable || []).map((u) => (
            <div key={u.run_id} className="border-b p-4" style={{ borderColor: "var(--bp-border)" }} data-testid={`unreachable-row-${u.brand_id}`}>
              <div className="flex justify-between items-baseline mb-2 gap-3">
                <div>
                  <div className="font-serif text-lg">{u.brand?.display_name || u.brand_id}</div>
                  <div className="text-xs" style={{ color: "var(--bp-text-muted)" }}>{u.brand?.website_url}</div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-xs font-mono text-amber-800">{u.outcome}</div>
                  {u.eligible_access_restricted && (
                    <button
                      onClick={() => publishAccessRestricted(u.run_id, u.brand?.display_name)}
                      className="text-xs inline-flex items-center gap-1 px-3 py-1.5 bg-neutral-900 text-white hover:bg-neutral-800 transition-colors"
                      data-testid={`publish-access-restricted-${u.brand_id}`}
                      title="Publish an ACCESS_RESTRICTED profile (no assessment, sources listed)"
                    >
                      Publish as Access-Restricted
                    </button>
                  )}
                </div>
              </div>
              <div className="text-sm mb-2">{u.held_reason}</div>
              {u.blocked_breakdown_counts && Object.keys(u.blocked_breakdown_counts).length > 0 && (
                <div className="text-xs font-mono mb-2" style={{ color: "var(--bp-text-muted)" }} data-testid={`breakdown-${u.brand_id}`}>
                  Blocked: {Object.entries(u.blocked_breakdown_counts).filter(([, n]) => n > 0).map(([k, n]) => `${k}=${n}`).join(" · ")}
                </div>
              )}
              <details>
                <summary className="text-xs cursor-pointer" style={{ color: "var(--bp-text-muted)" }}>Fetch log ({u.events?.length || 0} events)</summary>
                <ul className="mt-2 space-y-1 text-[11px] font-mono">
                  {(u.events || []).map((e, i) => (
                    <li key={`${e.at || ""}-${e.stage || ""}-${e.event || ""}-${i}`} className="border-l-2 border-neutral-300 pl-2">
                      <span className="text-neutral-500">{(e.at || "").slice(11, 19)}</span>{" "}
                      <b>[{e.stage}]</b> {e.event}{" "}
                      <span className="text-neutral-600">{JSON.stringify(e.detail).slice(0, 500)}</span>
                    </li>
                  ))}
                </ul>
              </details>
            </div>
          ))}
        </div>

        {/* Held updates */}
        <h2 className="font-serif text-2xl mt-10 mb-3">Held updates (anomaly guard)</h2>
        <div className="bg-white border" style={{ borderColor: "var(--bp-border)" }}>
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left" style={{ borderColor: "var(--bp-border)" }}>
              <th className="px-4 py-3 uppercase text-xs tracking-widest">Created</th>
              <th className="px-4 py-3 uppercase text-xs tracking-widest">Brand</th>
              <th className="px-4 py-3 uppercase text-xs tracking-widest">Reasons</th>
              <th className="px-4 py-3 uppercase text-xs tracking-widest">Flips</th>
              <th />
            </tr></thead>
            <tbody>
              {(d.held_updates || []).map((h) => (
                <tr key={h.id} className="border-b" style={{ borderColor: "var(--bp-border)" }} data-testid={`held-row-${h.id}`}>
                  <td className="px-4 py-3 font-mono text-xs">{(h.created_at || "").slice(0, 10)}</td>
                  <td className="px-4 py-3">{h.brand?.display_name || "—"}</td>
                  <td className="px-4 py-3 text-xs">{(h.reasons || []).join(", ")}</td>
                  <td className="px-4 py-3 font-mono text-xs">{h.flips || 0}</td>
                  <td className="px-4 py-3 text-right"><button onClick={() => releaseHeld(h.id)} className="text-xs underline inline-flex items-center gap-1"><Unlock size={12} /> Release &amp; reprocess</button></td>
                </tr>
              ))}
              {(d.held_updates || []).length === 0 && <tr><td colSpan={5} className="px-4 py-4 text-center" style={{ color: "var(--bp-text-muted)" }}>No held updates.</td></tr>}
            </tbody>
          </table>
        </div>

        {/* Dead letter */}
        <h2 className="font-serif text-2xl mt-10 mb-3">Dead-letter</h2>
        <div className="bg-white border" style={{ borderColor: "var(--bp-border)" }}>
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left" style={{ borderColor: "var(--bp-border)" }}>
              <th className="px-4 py-3 uppercase text-xs tracking-widest">At</th>
              <th className="px-4 py-3 uppercase text-xs tracking-widest">Stage</th>
              <th className="px-4 py-3 uppercase text-xs tracking-widest">URL / brand</th>
              <th className="px-4 py-3 uppercase text-xs tracking-widest">Reason</th>
              <th />
            </tr></thead>
            <tbody>
              {(d.dead_letter || []).map((x) => (
                <tr key={x.id} className="border-b" style={{ borderColor: "var(--bp-border)" }} data-testid={`dl-row-${x.id}`}>
                  <td className="px-4 py-3 font-mono text-xs">{(x.at || "").slice(0, 16)}</td>
                  <td className="px-4 py-3 font-mono text-xs">{x.stage}</td>
                  <td className="px-4 py-3 text-xs break-all">{x.url || x.brand_id}</td>
                  <td className="px-4 py-3 text-xs">{x.reason}</td>
                  <td className="px-4 py-3 text-right">
                    {x.brand_id && !x.retried_at && <button onClick={() => retryDL(x.id)} className="text-xs underline inline-flex items-center gap-1" data-testid={`dl-retry-${x.id}`}><RefreshCw size={12} /> Retry</button>}
                    {x.retried_at && <span className="text-xs" style={{ color: "var(--bp-text-muted)" }}>retried</span>}
                  </td>
                </tr>
              ))}
              {(d.dead_letter || []).length === 0 && <tr><td colSpan={5} className="px-4 py-4 text-center" style={{ color: "var(--bp-text-muted)" }}>No dead-letter items.</td></tr>}
            </tbody>
          </table>
        </div>

        {/* Controls */}
        <h2 className="font-serif text-2xl mt-10 mb-3">Controls</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="p-5 bg-white border" style={{ borderColor: "var(--bp-border)" }}>
            <div className="uppercase text-xs tracking-widest mb-3" style={{ color: "var(--bp-text-muted)" }}>Per-category extraction toggle</div>
            <ul className="space-y-1 text-sm">
              {CATS.map((c) => {
                const disabled = (cfg.disabled_categories || []).includes(c);
                return (
                  <li key={c} className="flex justify-between items-center">
                    <span>{c}</span>
                    <button onClick={() => toggleCat(c)} className={`px-2 py-0.5 text-xs uppercase border ${disabled ? "bg-neutral-200" : "bg-white"}`} style={{ borderColor: "var(--bp-border)" }} data-testid={`toggle-cat-${c}`}>{disabled ? "DISABLED" : "ENABLED"}</button>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="p-5 bg-white border" style={{ borderColor: "var(--bp-border)" }}>
            <div className="uppercase text-xs tracking-widest mb-3" style={{ color: "var(--bp-text-muted)" }}>Crawl blocklist (takedown requests)</div>
            <form onSubmit={addBlock} className="flex gap-2 mb-3">
              <Input placeholder="domain.com" value={newBlock.domain} onChange={(e) => setNewBlock({ ...newBlock, domain: e.target.value })} data-testid="blocklist-domain" />
              <Input placeholder="reason (optional)" value={newBlock.reason} onChange={(e) => setNewBlock({ ...newBlock, reason: e.target.value })} data-testid="blocklist-reason" />
              <Button type="submit" className="rounded-none" data-testid="blocklist-add">Block</Button>
            </form>
            <ul className="space-y-1 text-sm">
              {(cfg.blocklist || []).map((b) => (
                <li key={b.domain} className="flex justify-between items-center border-b py-1" style={{ borderColor: "var(--bp-border)" }}>
                  <span className="font-mono">{b.domain} <span className="text-xs" style={{ color: "var(--bp-text-muted)" }}>· {b.reason}</span></span>
                  <button onClick={() => rmBlock(b.domain)} className="text-xs underline" data-testid={`blocklist-rm-${b.domain}`}>remove</button>
                </li>
              ))}
              {(cfg.blocklist || []).length === 0 && <li style={{ color: "var(--bp-text-muted)" }}>Blocklist empty.</li>}
            </ul>
          </div>
        </div>

        {/* Config history */}
        <div className="mt-6 text-xs" style={{ color: "var(--bp-text-muted)" }}>
          Pipeline version: <span className="font-mono">{cfg.pipeline_version}</span> · Prompt version: <span className="font-mono">{cfg.prompt_version}</span> · Config changes: {cfg.history?.length || 0}
        </div>
      </div>

      {/* Persistent recrawl progress toast (item 1) — bottom-right corner.
          Polls run states every 4s while the batch is in flight, then auto-dismisses. */}
      {recrawlProgress && (
        <div
          className="fixed bottom-6 right-6 z-50 shadow-lg border p-4"
          style={{
            background: "white", borderColor: "var(--bp-border)",
            minWidth: 260, maxWidth: 360,
          }}
          data-testid="recrawl-progress-toast"
        >
          <div className="flex items-baseline justify-between mb-2">
            <div className="uppercase text-[10px] tracking-widest" style={{ color: "var(--bp-text-muted)" }}>
              Recrawl · {recrawlProgress.sector}
            </div>
            <button
              onClick={() => setRecrawlProgress(null)}
              className="text-xs bp-link"
              data-testid="recrawl-progress-dismiss"
              title="Dismiss"
            >
              ✕
            </button>
          </div>
          <div className="text-sm font-mono mb-2" data-testid="recrawl-progress-count">
            {recrawlProgress.done}/{recrawlProgress.total} completed
          </div>
          <div className="h-1.5 w-full" style={{ background: "#EEE" }}>
            <div
              className="h-full transition-all"
              style={{
                width: `${Math.round((recrawlProgress.done / Math.max(1, recrawlProgress.total)) * 100)}%`,
                background: recrawlProgress.done >= recrawlProgress.total ? "#166534" : "#0369A1",
              }}
            />
          </div>
          {recrawlProgress.done >= recrawlProgress.total && (
            <div className="mt-2 text-xs" style={{ color: "#166534" }}>
              All runs terminal. Reload the Pipeline monitor to review outcomes.
            </div>
          )}
        </div>
      )}
    </AdminLayout>
  );
}
